/**
 * Shared display rules for a `pricing_tiers` section's tier cards.
 *
 * Two renderers read the same DB rows — `components/homepage/PricingTiers.tsx`
 * (the tenant homepage section) and `components/tenant-marketing/TenantPricingPage.tsx`
 * (`/pricing`) — and they used to disagree about what `price` means. The wizard's
 * generator emits a NUMBER (`price: 39`, `0` = free), while hand-edited rows and
 * the admin builder carry labels (`"$39"`, `"Custom"`). The homepage printed the
 * raw value, so generator-seeded tiers rendered a bare `39`; `/pricing` only
 * showed the `/interval` suffix for numeric prices, so a `"$39"` row lost its
 * `/month`. Both now go through `formatTierPrice`.
 */

/** A currency-looking label can carry a `/month` suffix; "Custom" cannot. */
const AMOUNT_LABEL = /^[$€£¥]|^\d/;

export interface TierPriceDisplay {
  /** What to print in the price slot. */
  label: string;
  /** True when the label is an actual amount, so `/interval` makes sense. */
  isAmount: boolean;
}

export function formatTierPrice(
  price: number | string | null | undefined,
  freeLabel = 'Free',
): TierPriceDisplay {
  if (typeof price === 'number') {
    return price > 0 ? { label: `$${price}`, isAmount: true } : { label: freeLabel, isAmount: false };
  }
  const label = typeof price === 'string' ? price.trim() : '';
  // A nullish/empty price must read as free, never as an empty price slot.
  if (!label) return { label: freeLabel, isAmount: false };
  return { label, isAmount: AMOUNT_LABEL.test(label) };
}

/**
 * One reading of a stored tier, whatever wrote it.
 *
 * Three writers have touched `pricing_tiers` rows and each used its own keys:
 * the wizard and `/pricing` use `featured` / `cta_text` / `cta_link` and a
 * `features` array; the homepage section read `isPopular` / `cta` / `ctaLink`;
 * the admin builder wrote `highlighted`, `period` and `features` as one
 * newline-separated string. The homepage therefore showed wizard tiers with no
 * button and no "most popular" mark, and a tier saved from the admin builder
 * crashed it (`features.map` on a string).
 *
 * The wizard's shape is the stored one. These helpers read any of the three,
 * so existing rows render correctly without a data migration.
 */
export function tierFeatures(raw: unknown): string[] {
  const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? raw.split('\n') : [];
  return list.map((f) => (typeof f === 'string' ? f.trim() : '')).filter(Boolean);
}

export function tierIsFeatured(tier: Record<string, unknown> | null | undefined): boolean {
  return !!(tier?.featured || tier?.isPopular || tier?.highlighted);
}

export function tierCta(
  tier: Record<string, unknown> | null | undefined,
): { text: string; link: string } | null {
  const text = tier?.cta_text || tier?.cta;
  if (typeof text !== 'string' || !text.trim()) return null;
  const link = tier?.cta_link || tier?.ctaLink;
  return { text: text.trim(), link: typeof link === 'string' && link.trim() ? link.trim() : '#' };
}

/** `month`, from either `interval: "month"` or the builder's old `period: "/mo"`. */
export function tierInterval(tier: Record<string, unknown> | null | undefined): string {
  const raw = tier?.interval || tier?.period;
  return typeof raw === 'string' ? raw.trim().replace(/^\//, '') : '';
}
